import { Provider } from '@angular/core';
import { LocaleId } from '../../models/locale/locale-id.enum';
import { TranslationDictionaryMapping } from '../../models/translation-dictionary/translation-dictionary-mapping.type';
import { TranslationDictionary } from '../../models/translation-dictionary/translation-dictionary.type';
import { TranslateService } from '../../services/translate/translate.service';
import { TRANSLATION_DICTIONARY_MAPPING } from '../../tokens/translation-dictionary-mapping.token';

export function provideTranslationDictionaryMapping(
  translationDictionaryMapping?: TranslationDictionaryMapping,
): Provider {
  return [
    TranslateService,
    {
      provide: TRANSLATION_DICTIONARY_MAPPING,
      useValue: translationDictionaryMapping ?? {},
      multi: true,
    },
  ];
}

export function mergeTranslationDictionaryMappings<
  T extends TranslationDictionary,
>(
  ...translationDictionaryMappings: TranslationDictionaryMapping<T>[]
): TranslationDictionaryMapping<T> {
  const localeIds: LocaleId[] = [
    ...new Set(
      translationDictionaryMappings.reduce(
        (
          keys: LocaleId[],
          translationDictionaryMapping: TranslationDictionaryMapping<T>,
        ) => [
          ...keys,
          ...(Object.keys(translationDictionaryMapping) as LocaleId[]),
        ],
        [],
      ),
    ),
  ];

  return localeIds.reduce(
    (
      mergedTranslationMappings: TranslationDictionaryMapping<T>,
      localeId: LocaleId,
    ) => {
      const localeTranslationDictionaries: (T | undefined)[] =
        translationDictionaryMappings.map(
          (translationDictionaryMapping: TranslationDictionaryMapping<T>) =>
            translationDictionaryMapping[localeId],
        );
      return {
        ...mergedTranslationMappings,
        [localeId]: mergeTranslationDictionaries(
          ...localeTranslationDictionaries,
        ),
      };
    },
    {},
  );
}

export function mergeTranslationDictionaries<T extends TranslationDictionary>(
  ...translationDictionaries: (T | undefined)[]
): T {
  return translationDictionaries.reduce(
    (target: T, source?: T) => ({
      ...target,
      ...(source ?? {}),
    }),
    {} as T,
  );
}
